/**
 * Course Editor - LMS
 * Edit course details and manage modules
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import ModuleBlockEditor from './ModuleBlockEditor';

function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Course fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [certificateType, setCertificateType] = useState('iso');
  const [categoryId, setCategoryId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [modules, setModules] = useState([]);
  const [isScorm, setIsScorm] = useState(false);
  const [scormTitle, setScormTitle] = useState('');

  const [categories, setCategories] = useState([]);

  // Module form state
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleTitle, setModuleTitle] = useState('');
  const [moduleContent, setModuleContent] = useState('');

  useEffect(() => {
    axiosClient.get('/courses/categories').then(r => setCategories(r.data || [])).catch(() => {});
    if (courseId !== 'new') {
      fetchCourse();
    } else {
      setLoading(false);
    }
  }, [courseId]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/courses/courses/${courseId}`);
      const data = response.data;

      setTitle(data.title || '');
      setDescription(data.description || '');
      setCertificateType(data.certificate_type || 'iso');
      setCategoryId(data.category_id || '');
      setIsActive(data.is_active !== false);
      setModules(data.modules || []);
      setIsScorm(data.is_scorm || false);
      setScormTitle(data.scorm_title || '');
      setError(null);
    } catch (err) {
      console.error('Error fetching course:', err);
      setError('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    if (!title.trim()) {
      alert('Course title is required');
      return;
    }

    setSaving(true);
    try {
      const courseData = {
        title,
        description,
        certificate_type: certificateType,
        category_id: categoryId ? parseInt(categoryId) : null,
        is_active: isActive
      };

      const response = courseId === 'new'
        ? await axiosClient.post('/courses/courses/', courseData)
        : await axiosClient.put(`/courses/courses/${courseId}`, courseData);

      const data = response.data;

      alert('Course saved successfully');

      if (courseId === 'new') {
        navigate(`/courses/${data.id}`);
      } else {
        fetchCourse();
      }
    } catch (err) {
      console.error('Error saving course:', err);
      alert('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleAddModule = () => {
    setEditingModule(null);
    setModuleTitle('');
    setModuleContent('');
    setShowModuleForm(true);
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleTitle(module.title);
    setModuleContent(module.content);
    setShowModuleForm(true);
  };

  const handleSaveModule = async () => {
    if (!moduleTitle.trim() || !moduleContent.trim()) {
      alert('Module title and content are required');
      return;
    }

    try {
      const moduleData = {
        title: moduleTitle,
        content: moduleContent
      };

      if (editingModule) {
        // Update existing module
        await axiosClient.put(`/courses/modules/${editingModule.id}`, moduleData);
      } else {
        // Add new module
        await axiosClient.post(`/courses/${courseId}/modules`, moduleData);
      }

      alert('Module saved successfully');
      setShowModuleForm(false);
      fetchCourse();
    } catch (err) {
      console.error('Error saving module:', err);
      alert('Failed to save module');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!confirm('Delete this module?')) return;

    try {
      await axiosClient.delete(`/courses/modules/${moduleId}`);

      alert('Module deleted successfully');
      fetchCourse();
    } catch (err) {
      console.error('Error deleting module:', err);
      alert('Failed to delete module');
    }
  };

  const handleMoveModule = async (moduleId, direction) => {
    const currentIndex = modules.findIndex(m => m.id === moduleId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= modules.length) return;

    const reorderedModules = [...modules];
    const [movedModule] = reorderedModules.splice(currentIndex, 1);
    reorderedModules.splice(newIndex, 0, movedModule);

    const reorderData = reorderedModules.map((module, index) => ({
      module_id: module.id,
      order_index: index
    }));

    try {
      await axiosClient.put(`/courses/${courseId}/modules/reorder`, { modules: reorderData });

      fetchCourse();
    } catch (err) {
      console.error('Error reordering modules:', err);
      alert('Failed to reorder modules');
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginLeft: '8px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: '#fff',
    border: '2px solid #d4af37'
  };

  const sectionStyle = {
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px'
  };

  const labelStyle = {
    display: 'block',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '8px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '1rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    fontFamily: 'inherit'
  };

  const selectStyle = {
    ...inputStyle
  };

  const checkboxStyle = {
    marginRight: '8px',
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  };

  const moduleCardStyle = {
    background: '#f9f9f9',
    border: '1px solid #d4af37',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '12px'
  };

  const actionButtonStyle = {
    padding: '6px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    background: '#fff',
    color: '#1a1a1a',
    cursor: 'pointer',
    marginRight: '8px'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={sectionStyle}>Loading course...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>{courseId === 'new' ? 'Create Course' : 'Edit Course'}</h1>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            Manage course details and modules
          </p>
        </div>
        <div>
          <button onClick={() => navigate('/courses')} style={secondaryButtonStyle}>
            Back
          </button>
          <button onClick={handleSaveCourse} disabled={saving} style={buttonStyle}>
            {saving ? 'Saving...' : 'Save Course'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {/* SCORM info banner */}
      {isScorm && (
        <div style={{
          background: '#e3f2fd',
          border: '2px solid #1565c0',
          borderRadius: '8px',
          padding: '16px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ fontSize: '1.5rem' }}>📦</span>
          <div>
            <div style={{ fontWeight: '700', color: '#1565c0', fontSize: '1rem' }}>
              SCORM Course
            </div>
            <div style={{ color: '#1976d2', fontSize: '0.9rem', marginTop: '2px' }}>
              This course is powered by a SCORM package
              {scormTitle ? `: "${scormTitle}"` : ''}.
              Use the SCORM Importer to replace or update the package.
            </div>
          </div>
        </div>
      )}

      {/* Course Details Section */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
          Course Details
        </h2>

        <label style={labelStyle}>
          Course Title *
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            placeholder="Enter course title"
          />
        </label>

        <label style={labelStyle}>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={textareaStyle}
            placeholder="Enter course description"
          />
        </label>

        <label style={labelStyle}>
          Certificate Type *
          <select
            value={certificateType}
            onChange={(e) => setCertificateType(e.target.value)}
            style={selectStyle}
          >
            <option value="iso">ISO</option>
            <option value="cpd">CPD</option>
            <option value="pcaf">PCAF</option>
            <option value="assessment">Assessment</option>
          </select>
        </label>

        <label style={labelStyle}>
          Category
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            style={selectStyle}
          >
            <option value="">No Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </label>

        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            style={checkboxStyle}
          />
          Active (visible to users)
        </label>
      </div>

      {/* Modules Section */}
      {courseId !== 'new' && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#1a1a1a' }}>
              Course Modules ({modules.length})
            </h2>
            <button onClick={handleAddModule} style={buttonStyle}>
              + Add Module
            </button>
          </div>

          {modules.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#666' }}>
              No modules yet. Add your first module to get started.
            </div>
          ) : (
            modules.map((module, index) => (
              <div key={module.id} style={moduleCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', color: '#1a1a1a' }}>
                      {index + 1}. {module.title}
                    </h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                      {module.content.substring(0, 100)}...
                    </p>
                  </div>
                  <div style={{ marginLeft: '16px' }}>
                    {index > 0 && (
                      <button onClick={() => handleMoveModule(module.id, 'up')} style={actionButtonStyle}>
                        ↑
                      </button>
                    )}
                    {index < modules.length - 1 && (
                      <button onClick={() => handleMoveModule(module.id, 'down')} style={actionButtonStyle}>
                        ↓
                      </button>
                    )}
                    <button onClick={() => handleEditModule(module)} style={actionButtonStyle}>
                      Edit
                    </button>
                    <button onClick={() => handleDeleteModule(module.id)} style={actionButtonStyle}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Module Form Modal */}
      {showModuleForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            border: '2px solid #d4af37',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
              {editingModule ? 'Edit Module' : 'Add Module'}
            </h2>

            <label style={labelStyle}>
              Module Title *
              <input
                type="text"
                value={moduleTitle}
                onChange={(e) => setModuleTitle(e.target.value)}
                style={inputStyle}
                placeholder="Enter module title"
              />
            </label>

            {editingModule ? (
              <>
                <h3 style={{ marginTop: '20px', color: '#d4af37', fontSize: '1.2rem', marginBottom: '16px' }}>
                  Module Content Builder
                </h3>
                <ModuleBlockEditor moduleId={editingModule.id} />
              </>
            ) : (
              <label style={labelStyle}>
                Module Content *
                <textarea
                  value={moduleContent}
                  onChange={(e) => setModuleContent(e.target.value)}
                  style={{ ...textareaStyle, minHeight: '200px' }}
                  placeholder="Enter module content (you can add rich content blocks after creating the module)"
                />
              </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowModuleForm(false)} style={secondaryButtonStyle}>
                Cancel
              </button>
              <button onClick={handleSaveModule} style={buttonStyle}>
                Save Module
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseEditor;
